/**
 * Claude's Work view in Agora (Phase 8b): sessions, their event streams, the drawer, and
 * when it opens. Its own small store, so a busy stream re-renders only the Work view.
 */
import type { OpenedFile, PromptAnswer, WorkCommand, WorkEvent } from "@agora/agent-stream";
import { useSyncExternalStore } from "react";
import type { ViewMode } from "../workview";
import { api } from "./api";

export interface SessionView {
  id: string;
  kind: "project" | "room" | "quick";
  title: string;
  projectId: string | null;
  roomId: string | null;
  branch: string | null;
  mode: string;
  live: boolean;
  busy: boolean;
  openPrompts: number;
  lastSeq: number;
  lastTurnAt: string | null;
  totals: Record<string, number> | null;
  auth: Record<string, unknown> | null;
}

export interface ClaudeAuth {
  ok: boolean;
  apiKeySource: string;
  email?: string;
  subscriptionType?: string;
  problem?: string;
  sdk?: string;
}

interface WorkStore {
  sessions: Record<string, SessionView>;
  events: Record<string, WorkEvent[]>;
  /** The session of each room, once looked up (null: Claude has not worked there yet). */
  roomSession: Record<string, string | null>;
  drawer: string | null;
  mode: ViewMode;
  auth: ClaudeAuth | null;
  online: boolean;
  reason: string;
}

const MODE_KEY = "agora.workview.mode";
const savedMode = (): ViewMode => {
  try {
    const m = localStorage.getItem(MODE_KEY);
    return m === "hidden" || m === "dig" ? m : "auto";
  } catch {
    return "auto";
  }
};

let work: WorkStore = { sessions: {}, events: {}, roomSession: {}, drawer: null, mode: savedMode(), auth: null, online: false, reason: "" };
const listeners = new Set<() => void>();
function set(patch: Partial<WorkStore> | ((s: WorkStore) => Partial<WorkStore>)) {
  work = { ...work, ...(typeof patch === "function" ? patch(work) : patch) };
  listeners.forEach((l) => l());
}

export function useWork<T>(select: (s: WorkStore) => T): T {
  return useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => select(work),
  );
}

/** The app's router, so a "dig" view can take the owner to the full page. */
let go: ((path: string) => void) | null = null;
export function useWorkNavigation(navigate: (path: string) => void): void {
  go = navigate;
}

export const EMPTY_EVENTS: WorkEvent[] = [];

export async function loadSessions(): Promise<void> {
  const r = await api<{ sessions: SessionView[]; auth: ClaudeAuth | null; online: boolean; reason: string }>("/api/claude/sessions");
  set((s) => ({ sessions: { ...s.sessions, ...Object.fromEntries(r.sessions.map((x) => [x.id, x])) }, auth: r.auth, online: r.online, reason: r.reason }));
}

export async function sessionOfRoom(roomId: string): Promise<string | null> {
  const r = await api<{ session: SessionView | null }>(`/api/claude/rooms/${roomId}/session`);
  set((s) => ({ roomSession: { ...s.roomSession, [roomId]: r.session?.id ?? null }, ...(r.session ? { sessions: { ...s.sessions, [r.session.id]: r.session } } : {}) }));
  return r.session?.id ?? null;
}

const loading = new Map<string, Promise<void>>();
/** The session's events so far (after the last one held), then live ones arrive over the socket. */
export function loadEvents(sessionId: string): Promise<void> {
  const busy = loading.get(sessionId);
  if (busy) return busy;
  const p = (async () => {
    for (;;) {
      const held = work.events[sessionId] ?? [];
      const after = held.reduce((n, e) => (e.seq > n ? e.seq : n), 0);
      const r = await api<{ session: SessionView; events: WorkEvent[]; more: boolean }>(`/api/claude/sessions/${sessionId}/events?after=${after}`);
      set((s) => {
        const have = s.events[sessionId] ?? [];
        const seen = new Set(have.map((e) => e.seq));
        const add = r.events.filter((e) => !seen.has(e.seq));
        // Live events that came in while loading may be newer: keep the list in order.
        const merged = add.length ? [...have.filter((e) => e.seq !== -1), ...add].sort((a, b) => a.seq - b.seq) : have;
        return { events: { ...s.events, [sessionId]: merged }, sessions: { ...s.sessions, [sessionId]: r.session } };
      });
      if (!r.more) return;
    }
  })().finally(() => loading.delete(sessionId));
  loading.set(sessionId, p);
  return p;
}

/** Whether an event needs the owner (a card, an error, a failed turn, the plan used up). */
const needsYou = (e: WorkEvent) => e.kind === "prompt.opened" || e.kind === "error" || (e.kind === "turn.finished" && !e.ok) || (e.kind === "rate_limit" && (e.creditsRequired || e.status === "rejected"));

export function onWorkEvent(e: { type: "work.event"; sessionId: string; event: WorkEvent } | { type: "work.session"; session: SessionView }): void {
  if (e.type === "work.session") {
    set((s) => ({ sessions: { ...s.sessions, [e.session.id]: e.session } }));
    return;
  }
  const { sessionId, event } = e;
  set((s) => {
    const list = s.events[sessionId];
    const sess = s.sessions[sessionId];
    const patch: Partial<WorkStore> = {};
    // Only streams being watched keep events; the rest load from history when opened.
    if (list && !(event.seq > 0 && list.some((x) => x.seq === event.seq))) patch.events = { ...s.events, [sessionId]: [...list, event] };
    if (sess) {
      const busy = event.kind === "turn.started" || event.kind === "status" ? event.kind === "turn.started" || ["working", "waiting", "compacting", "starting"].includes(event.kind === "status" ? event.state : "") : event.kind === "turn.finished" ? false : sess.busy;
      const openPrompts = event.kind === "prompt.opened" ? sess.openPrompts + 1 : event.kind === "prompt.closed" ? Math.max(0, sess.openPrompts - 1) : sess.openPrompts;
      patch.sessions = { ...s.sessions, [sessionId]: { ...sess, busy, openPrompts, live: event.kind === "session.closed" ? false : true, lastSeq: Math.max(sess.lastSeq, event.seq) } };
    }
    return patch;
  });
  if (needsYou(event) && work.mode !== "hidden") {
    if (!work.events[sessionId]) void loadEvents(sessionId);
    if (work.mode === "dig" && go) go(`/work/${sessionId}`);
    else set({ drawer: sessionId });
  }
}

export function openDrawer(sessionId: string): void {
  set({ drawer: sessionId });
  void loadEvents(sessionId);
}

export function closeDrawer(): void {
  set({ drawer: null });
}

export function setViewMode(mode: ViewMode, sessionId?: string): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    // storage blocked: the choice lasts for this page only
  }
  set({ mode });
  if (mode === "dig" && sessionId && go) {
    set({ drawer: null });
    go(`/work/${sessionId}`);
  }
}

export async function answerPrompt(promptId: string, answer: PromptAnswer): Promise<void> {
  await api(`/api/claude/prompts/${promptId}/answer`, { answer }, "POST");
}

/** What the Work view sends, as Agora's routes. */
export async function sendWork(sessionId: string, cmd: WorkCommand): Promise<void> {
  switch (cmd.type) {
    case "message.send":
      await api(`/api/claude/sessions/${sessionId}/messages`, { text: cmd.text }, "POST");
      return;
    case "prompt.answer":
      await answerPrompt(cmd.promptId, cmd.answer);
      return;
    case "interrupt":
      await api(`/api/claude/sessions/${sessionId}/interrupt`, {}, "POST");
      return;
    case "mode.set":
      await api(`/api/claude/sessions/${sessionId}/mode`, { mode: cmd.mode }, "PUT");
      return;
    case "file.open":
      await openWorkFile(sessionId, cmd.path);
      return;
  }
}

export function openWorkFile(sessionId: string, path: string): Promise<OpenedFile> {
  return api<OpenedFile>(`/api/claude/sessions/${sessionId}/file?path=${encodeURIComponent(path)}`);
}
